import AddIcon from '@assets/images/add-icon.svg?react'
import DeviceIcon from '@assets/images/asset-device-icon.svg?react'
import AssetIntegrationIcon from '@assets/images/asset-integration-icon.svg?react'
import CloudIcon from '@assets/images/cloud-icon.svg?react'
import TechnologyIcon from '@assets/images/technology-icon.svg?react'
import AssetIcon from '@/ui/components/icons/asset-icon'
import { ASSET_TYPE_LABELS } from '@/core/modules/digital-footprint/assets/constants'
import { AssetDetails } from '@/ui/drawer/assetDetails'
import { BecIssueDetails } from '@/ui/drawer/becIssueDetails'
import { UserLeakedCredIssueDetails } from '@/ui/drawer/userLeakedCredIssueDetails'
import { AddDomainDrawer } from '@/ui/drawer/addDomainDrawer'
import { AddTechnologiesDrawer } from '@/ui/drawer/addTechnologiesDrawer'
import FdIssueDetails from '@/ui/drawer/fdIssueDetails/fdIssueDetails'
import { Integration } from '@/ui/drawer/integration'
import { InviteUser } from '@/ui/drawer/inviteUser'
import { IssueDetails } from '@/ui/drawer/issueDetails'
import { CaseDetails } from '@ui/drawer/caseDetails'
import { ServiceRequest } from '@ui/drawer/serviceRequestDetails'
import { TechnologyDetails } from '@/ui/drawer/technologyDetails'
import { DomainAssetIcon, UserAssetIcon } from '@atbay/icons'
import { getHighestSeverity } from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { ComponentType, ReactNode } from 'react'
import { Divider } from '@/ui/components/Divider/Divider.styles'
import Typography from '@/ui/components/typography'
import { DrawerHeaderConfig } from '@ui/drawer/common/DrawerNavigationHeader'
import { HeaderSeverityBadge } from '@ui/drawer/common/HeaderSeverityBadge'
import { issueDrawerHeaderConfig } from '@ui/drawer/common/issueDrawerHeaderConfig'
import {
    BadgesWrapper,
    LabelRow,
} from '@ui/drawer/common/drawerHeaderShared.styles'
import {
    caseDrawerHeaderConfig,
    serviceRequestDrawerHeaderConfig,
} from '@ui/drawer/common/caseDrawerHeaderConfig'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DrawerScreen = {
    header?: DrawerHeaderConfig<any>
    Body: ComponentType
}

const SEVERITY_TO_LABEL: Partial<
    Record<TIssueSeverity, 'critical' | 'important' | 'moderate'>
> = {
    critical: 'critical',
    important: 'important',
    moderate: 'moderate',
}

const renderSeverityBadge = (
    issues?: Array<{ status: string; severity: string }>
): ReactNode => {
    const severity = SEVERITY_TO_LABEL[getHighestSeverity(issues)]
    return severity ? <HeaderSeverityBadge severity={severity} /> : undefined
}

const renderHeaderLabel = (
    label: string,
    badges?: ReactNode,
    testId?: string
): ReactNode => (
    <LabelRow>
        <Typography variant="m-regular" testId={testId}>
            {label}
        </Typography>
        {badges && (
            <BadgesWrapper>
                <Divider
                    vertical
                    height="15px"
                    data-testid="header-badge-divider"
                />
                {badges}
            </BadgesWrapper>
        )}
    </LabelRow>
)

const getAssetIcon = (assetType?: TAssetType): ReactNode => {
    switch (assetType) {
        case 'domain':
        case 'subdomain':
        case 'email':
            return <DomainAssetIcon width={50} height={50} />
        case 'user':
            return <UserAssetIcon width={50} height={50} />
        case 'device':
            return <DeviceIcon width={20} height={20} />
        case 'cloud_app':
            return <CloudIcon width={25} height={25} />
        case 'integration':
            return <AssetIntegrationIcon width={22} height={22} />
        default:
            return assetType ? <AssetIcon asset_type={assetType} /> : null
    }
}

const assetDetailsHeader: DrawerHeaderConfig<TAsset> = {
    itemsKey: 'assets',
    indexKey: 'index',
    icon: (a) => getAssetIcon(a?.asset_type),
    label: (a) =>
        renderHeaderLabel(
            (a?.asset_type && ASSET_TYPE_LABELS[a.asset_type as TAssetType]) ||
                'Asset',
            renderSeverityBadge(a?.issues),
            a?.asset_type
                ? `drawer-asset-type-${a.asset_type}`
                : 'drawer-asset-type'
        ),
    title: (a) => a?.hostname || 'Asset details',
}

const addDomainHeader: DrawerHeaderConfig<string> = {
    itemsKey: 'addDomainItems',
    indexKey: 'addDomainIndex',
    navigable: false,
    icon: () => <AddIcon width={20} height={20} />,
    label: () => 'External Assets',
    title: () => 'Domain',
}

const addTechnologiesHeader: DrawerHeaderConfig<string> = {
    itemsKey: 'addTechnologiesItems',
    indexKey: 'addTechnologiesIndex',
    navigable: false,
    icon: () => <TechnologyIcon width={20} height={20} />,
    label: () => 'External Assets',
    title: () => 'Technology',
}

const technologyDetailsHeader: DrawerHeaderConfig<TTechnology> = {
    itemsKey: 'technologies',
    indexKey: 'currentIndex',
    icon: () => <TechnologyIcon width={20} height={20} />,
    label: (t) =>
        renderHeaderLabel(
            'Technology',
            renderSeverityBadge(t?.issues),
            `drawer-asset-type-technology`
        ),
    title: (t) => t?.name ?? 'Technology details',
}

export const drawerContentTypesMapping: {
    [K in DrawerContentType]: DrawerScreen
} = {
    issueDetails: { Body: IssueDetails, header: issueDrawerHeaderConfig },
    fdIssueDetails: {
        Body: FdIssueDetails,
        header: {
            ...issueDrawerHeaderConfig,
            title: () => 'Email Threat Detected',
        },
    },
    becIssueDetails: {
        Body: BecIssueDetails,
        header: {
            ...issueDrawerHeaderConfig,
            title: () => 'Suspected Mailbox Takeover',
        },
    },
    userLeakedCredIssueDetails: {
        Body: UserLeakedCredIssueDetails,
        header: issueDrawerHeaderConfig,
    },
    assetDetails: { Body: AssetDetails, header: assetDetailsHeader },
    caseDetails: { Body: CaseDetails, header: caseDrawerHeaderConfig },
    serviceRequestDetails: {
        Body: ServiceRequest,
        header: serviceRequestDrawerHeaderConfig,
    },
    technologyDetails: {
        Body: TechnologyDetails,
        header: technologyDetailsHeader,
    },
    addDomain: { Body: AddDomainDrawer, header: addDomainHeader },
    addTechnologies: {
        Body: AddTechnologiesDrawer,
        header: addTechnologiesHeader,
    },
    integration: { Body: Integration },
    inviteUser: { Body: InviteUser },
}

export type DrawerContentType =
    | 'issueDetails'
    | 'fdIssueDetails'
    | 'assetDetails'
    | 'addDomain'
    | 'addTechnologies'
    | 'integration'
    | 'inviteUser'
    | 'caseDetails'
    | 'serviceRequestDetails'
    | 'becIssueDetails'
    | 'userLeakedCredIssueDetails'
    | 'technologyDetails'

export const issueTypeToDrawerContentTypeMapping: {
    [K in TIssueType]: DrawerContentType
} = {
    FRAUD_DEFENSE: 'fdIssueDetails',
    INBOX_RULE: 'becIssueDetails',
    USER_LEAKED_CREDENTIALS: 'userLeakedCredIssueDetails',
    CLOUD_APP: 'issueDetails',
    GENERAL: 'issueDetails',
    DEVICE_INFOSTEALER: 'issueDetails',
}
